import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ReportTable, ReportTimeFilter } from '@erp/shared-types';
import { formatCurrency } from '@erp/shared-utils';
import GentlyTable, { type TableColumn } from '@/components/common/GentlyTable';
import {
  SingleSelect,
  type SingleSelectOption,
} from '@/components/common/SingleSelect';

interface ReportTableWrapperProps {
  table: ReportTable;
  timeFilter: ReportTimeFilter;
  onTimeFilterChange: (filter: ReportTimeFilter) => void;
  showTimeFilter?: boolean;
  downloadFilename?: string;
  tableName?: string;
  showTotals?: boolean;
  /** The year being viewed - used to determine month cutoff for past years */
  year?: number;
}

/**
 * Wrapper component that transforms ReportTable data for GentlyTable
 * Handles time filtering and column visibility
 */
export function ReportTableWrapper({
  table,
  tableName,
  timeFilter,
  onTimeFilterChange,
  showTimeFilter = true,
  downloadFilename,
  showTotals = false,
  year,
}: ReportTableWrapperProps) {
  // Determine which columns to show based on time filter
  const visibleColumnIndices = useMemo(() => {
    // Check if this is a month-based table
    const monthPattern =
      /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)(\s+\d{4})?$/;
    const monthColumnIndices: number[] = [];
    const nonMonthColumnIndices: number[] = [];

    table.columnHeaders.forEach((header, index) => {
      if (monthPattern.test(header)) {
        monthColumnIndices.push(index);
      } else {
        nonMonthColumnIndices.push(index);
      }
    });

    // If no month columns, show all columns
    if (monthColumnIndices.length === 0) {
      return Array.from({ length: table.columnHeaders.length }, (_, i) => i);
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    // For past years, treat 'current' and 'ytd' as December (full year)
    // For current year, use the actual current month
    const effectiveMonthIndex =
      year !== undefined && year < currentYear ? 11 : now.getMonth();
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    // Filter month columns based on time filter
    let visibleMonthIndices: number[] = [];
    switch (timeFilter) {
      case 'ytd': {
        // Show Jan through effective month (December for past years, current month for current year)
        visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
          const header = table.columnHeaders[colIndex];
          const monthMatch = header.match(
            /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/,
          );
          if (!monthMatch) return false;
          const monthName = monthMatch[1];
          const monthIdx = monthNames.indexOf(monthName);
          return monthIdx >= 0 && monthIdx <= effectiveMonthIndex;
        });
        break;
      }
      case 'current': {
        // Show only effective month (December for past years, current month for current year)
        const effectiveMonthName = monthNames[effectiveMonthIndex];
        visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
          const header = table.columnHeaders[colIndex];
          return header.startsWith(effectiveMonthName);
        });
        break;
      }
      case 'all':
      default: {
        // Show all month columns
        visibleMonthIndices = monthColumnIndices;
        break;
      }
    }

    // Combine non-month and visible month columns, maintaining order
    const allVisibleIndices: number[] = [];
    table.columnHeaders.forEach((_, index) => {
      if (
        nonMonthColumnIndices.includes(index) ||
        visibleMonthIndices.includes(index)
      ) {
        allVisibleIndices.push(index);
      }
    });

    return allVisibleIndices;
  }, [table.columnHeaders, timeFilter, year]);

  // Transform ReportTable data into GentlyTable row format
  const transformedData = useMemo(() => {
    return table.rows.map((row) => {
      const rowData: Record<
        string,
        string | number | { value: number; formattedValue: string }
      > = {
        _label: row.label,
        _id: row.id,
      };

      // Add visible columns with full cell data
      visibleColumnIndices.forEach((colIndex) => {
        const header = table.columnHeaders[colIndex];
        const cell = row.cells[colIndex];
        rowData[header] = cell
          ? { value: cell.value, formattedValue: cell.formattedValue }
          : { value: 0, formattedValue: '-' };
      });

      return rowData;
    });
  }, [table.rows, table.columnHeaders, visibleColumnIndices]);

  // Time filter options
  const timeFilterOptions: SingleSelectOption[] = useMemo(
    () => [
      { id: 'current', label: 'Current' },
      { id: 'ytd', label: 'YTD' },
      { id: 'all', label: 'All' },
    ],
    [],
  );

  // Generate column definitions for GentlyTable
  const columns = useMemo((): TableColumn<
    Record<string, string | number | { value: number; formattedValue: string }>
  >[] => {
    const cols: TableColumn<
      Record<
        string,
        string | number | { value: number; formattedValue: string }
      >
    >[] = [
      {
        id: '_label' as keyof Record<
          string,
          string | number | { value: number; formattedValue: string }
        >,
        label: table.title.includes('Supplier') ? 'Supplier' : 'Category',
        sortable: true,
        minWidth: '200px',
        render: (value) => (
          <span className="font-medium text-sm">{value as string}</span>
        ),
      },
    ];

    // Add column for each visible column header
    visibleColumnIndices.forEach((colIndex) => {
      const header = table.columnHeaders[colIndex];
      cols.push({
        id: header as keyof Record<
          string,
          string | number | { value: number; formattedValue: string }
        >,
        label: header,
        sortable: true,
        align: 'right',
        minWidth: '100px',
      });
    });

    return cols;
  }, [table.columnHeaders, table.title, visibleColumnIndices]);

  // Calculate totals for summary row
  const summaryRow = useMemo(() => {
    if (!showTotals) return undefined;

    const totals: Record<string, React.ReactNode> = {
      _label: <span className="font-bold text-sm">Total</span>,
    };

    // Calculate total for each visible column
    visibleColumnIndices.forEach((colIndex) => {
      const header = table.columnHeaders[colIndex];
      let columnTotal = 0;

      table.rows.forEach((row) => {
        const cell = row.cells[colIndex];
        if (cell && typeof cell.value === 'number') {
          columnTotal += cell.value;
        }
      });

      totals[header] = (
        <span className="font-bold text-sm">{formatCurrency(columnTotal)}</span>
      );
    });

    return totals;
  }, [showTotals, table.rows, table.columnHeaders, visibleColumnIndices]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg font-medium">
            {tableName ? tableName : table.title}
          </CardTitle>
          {showTimeFilter && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Time Period:
              </span>
              <SingleSelect
                options={timeFilterOptions}
                value={
                  timeFilterOptions.find((opt) => opt.id === timeFilter) || null
                }
                onChange={(option) => {
                  if (option) {
                    onTimeFilterChange(option.id as ReportTimeFilter);
                  }
                }}
                placeholder="Select time period..."
                size="sm"
                fullWidth={false}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="px-6 pb-6">
          <GentlyTable
            data={transformedData}
            columns={columns}
            showSearch={true}
            searchPlaceholder="Search..."
            searchFields={[
              '_label' as keyof Record<
                string,
                string | number | { value: number; formattedValue: string }
              >,
            ]}
            showDownload={true}
            downloadFilename={
              downloadFilename || table.title.toLowerCase().replace(/\s+/g, '-')
            }
            showCount={true}
            defaultSort={{
              sortBy: columns[columns.length - 1].id,
              sortDirection: 'desc',
            }}
            summaryRow={summaryRow}
            defaultCellRender={(value) => {
              if (
                typeof value === 'object' &&
                value !== null &&
                'formattedValue' in value
              ) {
                return (
                  <span className="text-sm font-medium">
                    {value.formattedValue}
                  </span>
                );
              }
              return <span className="text-sm">{value || '-'}</span>;
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
